import { formatDate } from './newsroom.js';
import { priceLabel } from './research-sponsor.js';

export const FAQ_QUESTIONS = 5;
export const MAX_ANSWER_WORDS = 130;

export const countWords = (text) =>
    (text || '').trim().split(/\s+/).filter(Boolean).length;

// The firm's name, or the site's own name when the profile has none to link.
export const websiteLabel = (firm, website) => {
    if (firm) return firm;
    try {
        return new URL(website).hostname.replace(/^www\./, '');
    } catch {
        return null;
    }
};

// Always five inputs in the Storify form; blanks are dropped by the API.
export const faqQuestionInputs = (questions) =>
    Array.from({ length: FAQ_QUESTIONS }, (_, i) => questions?.[i] ?? '');

// The story's one offer, once it has gone: the £50 FAQ offer, or the full sponsorship
// (the research sponsorship's re-offer). `notified` false: its email failed, Send retries it.
export const publicationOffer = (story, row) => {
    if (story?.faqOfferSentAt)
        return { choice: 'faq', at: story.faqOfferSentAt };
    if (row?.reofferOpenedAt) {
        return {
            choice: 'full',
            at: row.reofferOpenedAt,
            endsAt: row.reofferEndsAt,
            pricePence: row.pricePence,
            notified: Boolean(row.reofferNotifiedAt),
        };
    }
    return null;
};

// What Send would send: nothing once the offer has gone, unless its full sponsorship email failed.
export const offerSendChoice = (offer, choice) => {
    if (!offer) return choice;
    return offer.choice === 'full' && !offer.notified ? 'full' : 'none';
};

export const publicationOfferText = (offer) => {
    if (!offer) return null;
    if (offer.choice === 'faq') {
        return `FAQ sponsorship (£50+VAT) emailed to the responders on ${formatDate(
            offer.at
        )}. One offer per story.`;
    }
    const opened = `Full sponsorship offered at ${priceLabel(
        offer.pricePence
    )}+VAT on ${formatDate(
        offer.at
    )}, open to the respondents until ${formatDate(offer.endsAt)}.`;
    return offer.notified
        ? `${opened} One offer per story.`
        : `${opened} Its email did not go: send it again.`;
};

// As the API reads it: taking responses until its deadline. Stories are published once it
// closes, and the full sponsorship is offered only then (Dom, Asana #4, 4 Nov).
export const alertStillOpen = (alert, now = Date.now()) =>
    alert?.status === 'active' &&
    !(alert.deadline && new Date(alert.deadline).getTime() < now);

// Why Send can't go yet. The API checks the same and has the last word.
export const publicationOfferBlocker = ({
    choice,
    alert,
    story,
    row,
    dirty,
    pricePence,
    now = Date.now(),
}) => {
    if (!story?.publishedAt) return 'Publish the story first.';
    if (dirty)
        return 'Save the story first: the offer uses the saved questions.';
    if (!story.faqHeading || !story.faqQuestions?.length) {
        return 'Save the FAQ heading and at least one question first.';
    }
    if (story.faqSponsorStatus)
        return 'The expert answers slot is already taken.';
    if (row && row.status !== 'available') {
        return row.status === 'sold'
            ? 'The story sponsorship is already sold.'
            : 'A story sponsorship checkout is in progress.';
    }
    if (choice !== 'full') return null;
    if (alertStillOpen(alert, now)) {
        return 'Close the alert first: the full sponsorship goes out once it has closed.';
    }
    return pricePence ? null : 'Enter the price in whole pounds, up to £5,000.';
};

// The API gives a re-offer buyer the slot with the saved heading and questions.
export const reofferGrantBlocker = (story, draft) => {
    if (!story?.publishedAt) return 'Publish the story first.';
    if (!story.faqHeading || !story.faqQuestions?.length) {
        return 'Save the FAQ heading and at least one question first.';
    }
    const saved = [story.faqHeading, story.faqQuestions];
    const current = [
        draft.faqHeading.trim(),
        draft.faqQuestions.map((q) => q.trim()).filter(Boolean),
    ];
    return JSON.stringify(saved) === JSON.stringify(current)
        ? null
        : 'Save the story first: the buyer answers the saved questions.';
};

const STATUS_TEXT = {
    awaiting_answers: 'Paid, waiting for the sponsor’s answers',
    in_review: 'Answers in review',
    live: 'Expert answers live',
    declined: 'Declined',
    expired: 'Expired',
};

// For the Storify panel: where the story's sponsorship is, if anyone has paid.
export const faqSponsorStatusLabel = (story) => {
    const text = STATUS_TEXT[story?.faqSponsorStatus];
    if (!text) return null;
    const running = ['awaiting_answers', 'in_review', 'live'].includes(
        story.faqSponsorStatus
    );
    // No end date: it came free with a full sponsorship.
    const until = !running
        ? ''
        : story.faqSponsorExpiresAt
        ? ` · runs until ${formatDate(story.faqSponsorExpiresAt)}`
        : ' · runs with the story sponsorship';
    return `${text}${until}`;
};

// What the pay page says when the API turns a responder away.
export const sponsorCheckoutError = (response, renewing = false) => {
    if (response?.data?.code === 'ALREADY_SPONSOR') return 'yours';
    if (renewing && response?.status === 404) return 'nothingToRenew';
    if (response?.status === 409) return 'taken';
    if (response?.status === 403) return 'notResponder';
    if (response?.status === 404) return 'notOffered';
    return 'failed';
};
